import React, { ReactElement, ReactNode } from 'react';
import { Paragraph } from '../Paragraph';
import { Button } from '../Button';
import classes from './EmptyState.module.css';

type EmptyStateAction = {
  label: string;
  onClick: () => void;
  variant?: 'primary' | 'secondary' | 'tertiary';
  icon?: ReactNode;
};

type EmptyStateProgress = {
  current: number;
  total: number;
  label: string;
};

type EmptyStateProps = {
  icon?: ReactElement;
  title?: string;
  description?: string;
  // Legacy single action (backwards compatible)
  actionLabel?: string;
  onAction?: () => void;
  actionVariant?: 'primary' | 'secondary';
  actionSize?: 'small' | 'medium' | 'large';
  actionIcon?: ReactNode;
  // New multi-action support
  actions?: EmptyStateAction[];
  // Progress indicator for waiting states
  progress?: EmptyStateProgress;
  // Timeline/estimate text
  timeline?: string;
  className?: string;
  compact?: boolean;
};

export const EmptyState: React.FC<EmptyStateProps> = ({
  icon,
  title = 'Ingen data',
  description = 'Data vil vises her når du har gjennomført en undersøkelse.',
  actionLabel,
  onAction,
  actionVariant = 'primary',
  actionSize = 'medium',
  actionIcon,
  actions,
  progress,
  timeline,
  className,
  compact = false,
}) => {
  // Determine which actions to render (new multi-action or legacy single action)
  const actionsToRender = actions || (actionLabel && onAction ? [{
    label: actionLabel,
    onClick: onAction,
    variant: actionVariant,
    icon: actionIcon,
  }] : []);

  return (
    <div
      className={`${classes.emptyState} ${compact ? classes.compact : ''} ${className || ''}`}
      role="status"
      aria-live="polite"
    >
      {icon && <div className={classes.emptyIcon}>{icon}</div>}
      <div className={classes.emptyContent}>
        <Paragraph size="lg" className={classes.emptyTitle}>
          {title}
        </Paragraph>
        <Paragraph size="md" className={classes.emptyText}>
          {description}
        </Paragraph>

        {progress && (
          <div className={classes.progressSection}>
            <div className={classes.progressBar}>
              <div
                className={classes.progressFill}
                style={{
                  width: `${
                    progress.total > 0
                      ? Math.min(Math.max((progress.current / progress.total) * 100, 0), 100)
                      : 0
                  }%`
                }}
              />
            </div>
            <div className={classes.progressLabel}>
              {progress.current} av {progress.total} {progress.label}
            </div>
          </div>
        )}

        {timeline && (
          <Paragraph size="sm" className={classes.timeline}>
            {timeline}
          </Paragraph>
        )}

        {actionsToRender.length > 0 && (
          <div className={classes.actions}>
            {actionsToRender.map((action, index) => (
              <Button
                key={index}
                className={classes.button}
                onClick={action.onClick}
                type="button"
                size={actionSize}
                variant={action.variant || 'primary'}
              >
                <span>{action.label}</span>
                {action.icon}
              </Button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};